import { useState } from "react";
import clsx from "clsx";
import { useAuth0 } from "@auth0/auth0-react";
import { RequestInfoField } from "@/lib/interfaces and Enums/ENUMRequestInformationStatus";
import { IExecutionRequestInfoState } from "@/store/requestInformationStore";
import { respondToExecutionRequestInformation } from "@/store/requestInformationStoreACTIONS";
import { TRADE_SETTINGS_CONTROL_HEIGHT } from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import {
  REQUEST_INFO_FIELD_LABELS,
  getRequestedFields,
} from "./requestInformationStripHelpers";

// Accept and Decline must be EXACTLY the same size, so they share one base of
// structural classes — height, full width, radius, a 1px border, and the
// disabled style. Only the colours differ between them. Accept's border is
// transparent (rather than omitted) so its box model matches Decline's bordered
// box precisely — a missing border would otherwise make it a hair taller/wider.
const RESPONSE_BUTTON_BASE = clsx(
  TRADE_SETTINGS_CONTROL_HEIGHT,
  "w-full rounded-md border disabled:opacity-40",
);

// LQC-2271 - the strip deliberately does NOT follow the CP row's column
// template. With Side + Direction in the row that pushed Accept / Decline ~500px
// right, off the visible panel. Instead the label column is `max-content` (as
// wide as the longest "<CP> requests <field>" actually shown, so every chip's
// buttons still line up) and the buttons sit immediately after it at a fixed
// width. The strip then hugs its content at the left edge of the row, visible
// without scrolling.
const RESPONSE_BUTTON_WIDTH_PX = 84;
const STRIP_GRID_COLUMNS = `max-content ${RESPONSE_BUTTON_WIDTH_PX}px ${RESPONSE_BUTTON_WIDTH_PX}px`;

// LQC-637 — OP-side conditional Request Information strip. Renders directly
// beneath a CP's row in the condensed Trade Settings grid, and ONLY when that
// CP's execution request has one or more fields the RP has Requested. Each
// requested field is a chip with accept (✓) / reject (✕) controls wired to
// respondToExecutionRequestInformation (PATCH /Trade/Execution/Request). On
// response the field flips to Released/Rejected and drops out of the strip; when
// none remain, the strip renders nothing. The strip is a second route to the
// same settings: since LQC-2271 the OP can also release Direction directly from
// the grid's Direction column.

const RequestInformationStrip = ({
  esid,
  executionRequestId,
  statuses,
  cpCode,
  fontSizeClass,
}: {
  esid: string;
  executionRequestId: number | string;
  statuses: IExecutionRequestInfoState | undefined;
  cpCode: string;
  fontSizeClass?: string;
}) => {
  const { getAccessTokenSilently } = useAuth0();
  // Guard against double-clicks while a response is in flight; the optimistic
  // store stamp removes the chip on success, this just disables it meanwhile.
  const [inFlight, setInFlight] = useState<RequestInfoField[]>([]);

  const requestedFields = getRequestedFields(statuses);
  if (requestedFields.length === 0) return null;

  const respond = async (field: RequestInfoField, accepted: boolean) => {
    setInFlight((prev) => [...prev, field]);
    try {
      await respondToExecutionRequestInformation({
        esid,
        executionRequestId,
        field,
        accepted,
        getAccessTokenSilently,
      });
    } finally {
      setInFlight((prev) => prev.filter((f) => f !== field));
    }
  };

  return (
    <div
      data-testid={`RequestInformationStrip-${cpCode}`}
      id={`RequestInformationStrip-${cpCode}`}
      // Sits beneath the CP's settings row. The chips render as `contents`, so
      // this box IS the grid: one row per requested field, three columns shared
      // by all of them. w-fit so it hugs the chips at the left of the (wider,
      // scrolling) CP row.
      className={clsx(
        "mb-1 grid w-fit items-center gap-x-2 gap-y-1 rounded-md border border-gray-300 px-2 py-2 dark:border-gray-700",
        fontSizeClass,
      )}
      style={{ gridTemplateColumns: STRIP_GRID_COLUMNS }}
    >
      {requestedFields.map((field) => {
        const busy = inFlight.includes(field);
        const label = REQUEST_INFO_FIELD_LABELS[field];
        return (
          // `contents` puts the label + both buttons straight into the strip's
          // own grid, so the label column is sized once across every chip and
          // all the Accept buttons line up.
          <div
            key={field}
            data-testid={`RequestInformationChip-${cpCode}-${field}`}
            className="contents"
          >
            <span className="flex items-center gap-2 whitespace-nowrap">
              <span className="h-2.5 w-2.5 flex-none rounded-full bg-orange-500" />
              <span>
                {cpCode} requests <span className="font-semibold">{label}</span>
              </span>
            </span>
            <button
              type="button"
              data-testid={`RequestInformationAccept-${cpCode}-${field}`}
              aria-label={`Accept ${label} request from ${cpCode}`}
              disabled={busy}
              onClick={() => respond(field, true)}
              // Teal fill marks the primary action; transparent border keeps the
              // box identical to Decline. Dark text in dark mode for contrast.
              className={clsx(
                RESPONSE_BUTTON_BASE,
                "border-transparent bg-teal-500 font-medium text-white hover:bg-teal-600 dark:text-gray-900",
              )}
            >
              Accept
            </button>
            <button
              type="button"
              data-testid={`RequestInformationReject-${cpCode}-${field}`}
              aria-label={`Decline ${label} request from ${cpCode}`}
              disabled={busy}
              onClick={() => respond(field, false)}
              // Outlined, transparent fill; identical box to the Accept button.
              className={clsx(
                RESPONSE_BUTTON_BASE,
                "border-gray-300 bg-transparent hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700",
              )}
            >
              Decline
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default RequestInformationStrip;
